import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Stack,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Warning,
} from '@mui/icons-material';
import type { FlowUploadHeader } from '../PhotoUploader';
import { useDevice } from '../../contexts/DeviceContext';
import { FlowLoadingOverlay } from '../../flow/components/FlowLoadingOverlay';
import { BreakfastCard } from './BreakfastCard';
import { BreakfastButton, BreakfastButtonStyle, BreakfastButtonSize } from './BreakfastButton';
import { BreakfastProgressBar } from './BreakfastProgressBar';
import { BreakfastColors } from '../../theme/BreakfastTheme';
import { StudioFlowColors } from '../../flow/studioFlowTheme';
import { UploadFile, UploadSession } from '../../types';
import SessionStatus from '../SessionStatus';

const PREP_PROGRESS_SHARE = 0.15;

function filePipelineProgress(upload: UploadFile): number {
  if (upload.downloadUrl) {
    return 1;
  }
  if (upload.isUploading) {
    return PREP_PROGRESS_SHARE + (1 - PREP_PROGRESS_SHARE) * upload.progress;
  }
  if (upload.isPreparing) {
    return PREP_PROGRESS_SHARE * (upload.prepareProgress ?? 0);
  }
  return PREP_PROGRESS_SHARE;
}

function computeUploadProgress(uploads: UploadFile[]) {
  const total = uploads.length;
  if (total === 0) {
    return { completed: 0, barProgress: 0, preparingCount: 0 };
  }

  let weightedProgress = 0;
  for (const upload of uploads) {
    weightedProgress += filePipelineProgress(upload);
  }

  return {
    completed: uploads.filter((upload) => upload.downloadUrl).length,
    barProgress: (weightedProgress / total) * 100,
    preparingCount: uploads.filter((upload) => upload.isPreparing).length,
  };
}

function useMonotonicUploadProgress(uploads: UploadFile[]) {
  const maxCompletedRef = useRef(0);
  const maxBarProgressRef = useRef(0);
  const { completed, barProgress } = computeUploadProgress(uploads);

  if (uploads.length === 0) {
    maxCompletedRef.current = 0;
    maxBarProgressRef.current = 0;
  } else {
    maxCompletedRef.current = Math.max(maxCompletedRef.current, completed);
    maxBarProgressRef.current = Math.max(maxBarProgressRef.current, barProgress);
  }

  return {
    completedCount: maxCompletedRef.current,
    progressPercentage: Math.round(maxBarProgressRef.current),
    totalProgress: maxBarProgressRef.current / 100,
  };
}

interface BreakfastPhotoUploaderProps {
  // Session management
  session: UploadSession;
  
  // Upload state
  uploads: UploadFile[];
  isRetryingFailedBatch?: boolean;
  pendingSelectionCount?: number;
  flowMode?: boolean;
  onFlowHeaderChange?: (header: FlowUploadHeader) => void;

  // Upload callbacks
  onFileSelect: () => void;
  onFileInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onGooglePhotosSelect: () => void;
  onStartUpload: () => void;
  onRetryFailed?: () => void;
  onClearAll: () => void;
  onMakeMyBook?: () => void;
}

export const BreakfastPhotoUploader: React.FC<BreakfastPhotoUploaderProps> = ({
  session,
  uploads,
  isRetryingFailedBatch = false,
  pendingSelectionCount = 0,
  flowMode = false,
  onFlowHeaderChange,
  onFileSelect,
  onFileInputChange,
  onGooglePhotosSelect,
  onStartUpload,
  onRetryFailed,
  onClearAll,
  onMakeMyBook,
}) => {
  const { deviceInfo } = useDevice();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [makeMyBookClicked, setMakeMyBookClicked] = useState(false);
  
  // Computed values
  const hasSelection = uploads.length > 0;
  const hasPendingSelection = pendingSelectionCount > 0;
  const selectedUploadCount = Math.max(uploads.length, pendingSelectionCount);
  const allDone = uploads.every(u => u.downloadUrl);
  const preparingCount = uploads.filter((u) => u.isPreparing).length;
  const uploadsStarted = hasPendingSelection || (flowMode && hasSelection) || uploads.some(
    (u) => u.isPreparing || u.isUploading || u.downloadUrl || u.error,
  );
  const allUploadsComplete = hasSelection && allDone;
  const sessionCompleted = session.status === 'complete';
  const isBatchNumberLoaded = session.batchNumber > 0;
  
  // Photo validation
  const minPhotosRequired = session.minPhotos || 0;
  const maxPhotosAllowed = session.maxPhotos || 1000;
  const meetsMinimum = uploads.length >= minPhotosRequired;
  const exceedsMaximum = uploads.length > maxPhotosAllowed;
  const hasValidPhotoCount = meetsMinimum && !exceedsMaximum;
  
  const {
    completedCount: monotonicCompletedCount,
    progressPercentage,
    totalProgress,
  } = useMonotonicUploadProgress(uploads);
  const failedUploadCount = uploads.filter((u) => u.error).length;

  const formatBytes = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatSpeed = (bytesPerSecond: number) => {
    if (!bytesPerSecond || bytesPerSecond < 1) return null;
    return `${formatBytes(bytesPerSecond)}/s`;
  };

  const formatDuration = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds <= 0) return null;
    if (seconds < 60) return `about ${Math.ceil(seconds)}s remaining`;
    return `about ${Math.ceil(seconds / 60)}m remaining`;
  };
  
  const handleMakeMyBook = () => {
    setMakeMyBookClicked(true);
    onMakeMyBook?.();
  };

  useEffect(() => {
    if (!flowMode || !onFlowHeaderChange) return;

    if (uploadsStarted && !allUploadsComplete) {
      onFlowHeaderChange({
        title: 'Uploading photos',
        subtitle: 'Keep this page open until we are finished',
      });
      return;
    }

    if (allUploadsComplete) {
      onFlowHeaderChange({
        title: 'Upload complete',
        subtitle: 'Taking you to book options…',
      });
      return;
    }

    onFlowHeaderChange({ title: 'Upload your photos' });
  }, [flowMode, onFlowHeaderChange, uploadsStarted, allUploadsComplete]);

  const renderFlowUploadProgress = () => {
    if (!uploadsStarted || allUploadsComplete) return null;

    const successful = monotonicCompletedCount;
    const active = uploads.filter((u) => u.isUploading).length;
    const totalCount = selectedUploadCount;
    const isWaitingToStart = flowMode && hasSelection && active === 0 && successful === 0;
    const isPreparingBatch = hasPendingSelection || preparingCount > 0 || isWaitingToStart;
    const preparedCount = hasPendingSelection && uploads.length === 0 ?
      0 :
      Math.max(0, uploads.length - preparingCount);
    const showIndeterminate =
      hasPendingSelection ||
      (isPreparingBatch && progressPercentage === 0) ||
      (!isPreparingBatch &&
        (active > 0 || isRetryingFailedBatch) &&
        progressPercentage === 0);
    const statusLine = isPreparingBatch
      ? `Preparing ${preparedCount} of ${totalCount}…`
      : `${successful} of ${totalCount} uploaded${active > 0 && successful < totalCount ? '…' : ''}`;

    return (
      <Box sx={{ mb: 3 }}>
        {uploadsStarted && !allUploadsComplete && (
          <Box sx={{ maxWidth: 320, mx: 'auto' }}>
            <LinearProgress
              variant={showIndeterminate ? 'indeterminate' : 'determinate'}
              value={progressPercentage}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: StudioFlowColors.selectionStroke,
                '& .MuiLinearProgress-bar': {
                  bgcolor: StudioFlowColors.title,
                  borderRadius: 4,
                },
              }}
            />
            <Typography
              sx={{
                mt: 2,
                textAlign: 'center',
                fontWeight: 700,
                fontSize: 15,
                color: StudioFlowColors.title,
                fontFamily: StudioFlowColors.bodyFontFamily,
              }}
            >
              {statusLine}
            </Typography>
            {failedUploadCount > 0 && onRetryFailed && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: BreakfastColors.error,
                    mb: 1,
                    fontFamily: StudioFlowColors.bodyFontFamily,
                  }}
                >
                  {failedUploadCount} photo{failedUploadCount !== 1 ? 's' : ''} did not finish.
                </Typography>
                <BreakfastButton
                  title={isRetryingFailedBatch ? 'Retrying...' : `Retry ${failedUploadCount} failed`}
                  style={BreakfastButtonStyle.Secondary}
                  size={BreakfastButtonSize.Small}
                  onPressed={isRetryingFailedBatch ? undefined : onRetryFailed}
                  shrinkWrap
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderFlowUploadPlaceholder = () => {
    if (!flowMode || hasSelection || uploadsStarted || allUploadsComplete) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: { xs: 'calc(100vh - 300px)', md: 260 },
          pt: { xs: 2, md: 2 },
          pb: { xs: 2, md: 1 },
        }}
      >
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/images/upload-photos-placeholder.png`}
          alt=""
          aria-hidden="true"
          draggable={false}
          sx={{
            width: { xs: 244, md: 220 },
            maxWidth: '68vw',
            height: 'auto',
            display: 'block',
            userSelect: 'none',
          }}
        />
      </Box>
    );
  };

  const renderHeader = () => (
    <Box 
      display="flex" 
      alignItems="center" 
      mb={3} 
      px={{ xs: 2, sm: 3 }} 
      py={2}
    >
      <img 
        src="/images/studio-logo.svg" 
        alt="Chatbooks Studio" 
        style={{ height: 56, marginRight: 20 }}
      />
      <Box>
        <Typography 
          variant="h1" 
          component="h1"
          sx={{ 
            fontSize: '24px',
            fontWeight: 700,
            fontFamily: '"CircularXX", sans-serif',
            color: BreakfastColors.title,
            letterSpacing: '-0.5px',
            marginBottom: 0.5,
            lineHeight: 1.2
          }}
        >
          Chatbooks Studio Uploader
        </Typography>
        <Typography 
          variant="body1"
          sx={{ 
            fontSize: '14px',
          fontWeight: 400,
          fontFamily: '"CircularXX", sans-serif',
          color: BreakfastColors.subtitle,
          letterSpacing: '-0.1px'
        }}
      >
        v1.3.0
      </Typography>
      </Box>
    </Box>
  );

  const renderStatusCard = () => {
    if (flowMode || (hasSelection && session.isValid)) return null;
    
    return (
      <Box px={{ xs: 2, sm: 3 }}>
        <SessionStatus session={session} deviceInfo={deviceInfo} />
      </Box>
    );
  };

  const renderPhotoRequirementsWarning = () => {
    if (!session.isValid || sessionCompleted || allUploadsComplete || uploadsStarted) return null;
    if (!hasSelection || hasValidPhotoCount) return null;
    
    const bookTypeText = session.bookType || 'book';
    
    if (!meetsMinimum) {
      const photosNeeded = minPhotosRequired - uploads.length;
      return (
        <BreakfastCard backgroundColor={BreakfastColors.warning + '1A'}>
          <Box py={2}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Warning sx={{ color: BreakfastColors.warning, fontSize: 24 }} />
              <Typography 
                variant="body2"
                sx={{ 
                  fontSize: '16px',
                  fontWeight: 700,
                  fontFamily: '"CircularXX", sans-serif',
                  color: BreakfastColors.title
                }}
              >
                More Photos Needed
              </Typography>
            </Box>
            <Typography 
              variant="body1"
              sx={{ 
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: '"CircularXX", sans-serif',
                color: BreakfastColors.subtitle,
                marginBottom: 2
              }}
            >
              You need at least {minPhotosRequired} photos for a {bookTypeText}. 
              You currently have {uploads.length} photo{uploads.length !== 1 ? 's' : ''}. 
              Please add {photosNeeded} more photo{photosNeeded !== 1 ? 's' : ''} to proceed.
            </Typography>
            
            {/* Progress bar */}
            <Box mb={1}>
              <Typography 
                variant="caption"
                sx={{ 
                  fontSize: '12px',
                  fontWeight: 500,
                  fontFamily: '"CircularXX", sans-serif',
                  color: BreakfastColors.subtitle
                }}
              >
                Progress: {uploads.length}/{minPhotosRequired} photos
              </Typography>
            </Box>
            <BreakfastProgressBar 
              value={(uploads.length / minPhotosRequired) * 100} 
              color={BreakfastColors.warning}
            />
          </Box>
        </BreakfastCard>
      );
    }
    
    if (exceedsMaximum) {
      const photosOver = uploads.length - maxPhotosAllowed;
      return (
        <BreakfastCard backgroundColor={BreakfastColors.error + '1A'}>
          <Box py={2}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Warning sx={{ color: BreakfastColors.error, fontSize: 24 }} />
              <Typography 
                variant="body2"
                sx={{ 
                  fontSize: '16px',
                  fontWeight: 700,
                  fontFamily: '"CircularXX", sans-serif',
                  color: BreakfastColors.title
                }}
              >
                Too Many Photos
              </Typography>
            </Box>
            <Typography 
              variant="body1"
              sx={{ 
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: '"CircularXX", sans-serif',
                color: BreakfastColors.subtitle
              }}
            >
              You have {uploads.length} photos, but the maximum allowed for a {bookTypeText} is {maxPhotosAllowed}. 
              Please remove {photosOver} photo{photosOver !== 1 ? 's' : ''} to proceed.
            </Typography>
          </Box>
        </BreakfastCard>
      );
    }
    
    return null;
  };

  const renderMainUploadProgressBar = () => {
    if (flowMode || !uploadsStarted || allUploadsComplete) return null;

    if (hasPendingSelection && uploads.length === 0) {
      return (
        <BreakfastCard>
          <Box py={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '16px',
                  fontWeight: 700,
                  fontFamily: '"CircularXX", sans-serif',
                  color: BreakfastColors.title
                }}
              >
                Preparing {pendingSelectionCount} photo{pendingSelectionCount !== 1 ? 's' : ''}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  fontFamily: '"CircularXX", sans-serif',
                  color: BreakfastColors.primary
                }}
              >
                Preparing...
              </Typography>
            </Box>
            <LinearProgress
              variant="indeterminate"
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: BreakfastColors.defaultFill,
                '& .MuiLinearProgress-bar': {
                  bgcolor: BreakfastColors.primary,
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        </BreakfastCard>
      );
    }
    
    const successful = monotonicCompletedCount;
    const failed = uploads.filter(u => u.error).length;
    const active = uploads.filter(u => u.isUploading).length;
    const retrying = uploads.filter(u => u.isRetrying).length;
    const remaining = uploads.length - successful - failed;
    const totalBytes = uploads.reduce((sum, u) => sum + (u.totalBytes || u.compressedSize || u.file.size), 0);
    const uploadedBytes = uploads.reduce((sum, u) => {
      if (u.downloadUrl) return sum + (u.totalBytes || u.compressedSize || u.file.size);
      return sum + (u.uploadedBytes || 0);
    }, 0);
    const currentSpeed = uploads.reduce((sum, u) => sum + (u.uploadSpeedBytesPerSecond || 0), 0);
    const speedText = formatSpeed(currentSpeed);
    const etaText = formatDuration((totalBytes - uploadedBytes) / currentSpeed);
    const countSummary = [
      preparingCount > 0 ? `${uploads.length - preparingCount}/${uploads.length} prepared` : null,
      `${successful}/${uploads.length} complete`,
      active > 0 ? `${active} uploading` : null,
      retrying > 0 ? `${retrying} retrying` : null,
      failed > 0 ? `${failed} failed` : null,
    ].filter(Boolean).join(' · ');
    
    // Calculate time estimate
    const getTimeEstimate = () => {
      // Need at least one completed upload to estimate
      if (successful === 0) return null;
      
      // Calculate average time from completed uploads
      const completedUploads = uploads.filter(u => u.uploadStartTime && u.uploadEndTime);
      if (completedUploads.length === 0) return null;
      
      const totalTime = completedUploads.reduce((sum, u) => 
        sum + (u.uploadEndTime! - u.uploadStartTime!), 0
      );
      const avgTimePerUpload = totalTime / completedUploads.length;
      const estimatedRemainingTime = avgTimePerUpload * remaining;
      
      if (estimatedRemainingTime < 60000) { // Less than 1 minute
        const seconds = Math.ceil(estimatedRemainingTime / 1000);
        return `About ${seconds}s remaining`;
      } else {
        const minutes = Math.ceil(estimatedRemainingTime / 60000);
        return `About ${minutes}m remaining`;
      }
    };
    
    const timeEstimate = etaText || getTimeEstimate();
    const statusText = preparingCount > 0
      ? 'Preparing...'
      : (isRetryingFailedBatch
        ? 'Retrying...'
        : (active > 0 ? 'Uploading...' : (failed > 0 ? 'Needs retry' : 'Uploading...')));
    
    return (
      <BreakfastCard>
        <Box py={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography 
              variant="body2"
              sx={{ 
                fontSize: '16px',
                fontWeight: 700,
                fontFamily: '"CircularXX", sans-serif',
                color: BreakfastColors.title
              }}
            >
              Uploading {uploads.length} photos
            </Typography>
            <Typography 
              variant="body1"
              sx={{ 
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: '"CircularXX", sans-serif',
                color: BreakfastColors.subtitle
              }}
            >
              {countSummary || `${successful}/${uploads.length}`}
            </Typography>
          </Box>
          
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography 
              variant="body1"
              sx={{ 
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: '"CircularXX", sans-serif',
                color: BreakfastColors.subtitle
              }}
            >
              {timeEstimate || `${progressPercentage}% complete`}
            </Typography>
            <Typography 
              variant="body1"
              sx={{ 
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: '"CircularXX", sans-serif',
                color: BreakfastColors.primary
              }}
            >
              {statusText}
            </Typography>
          </Box>

          {(totalBytes > 0 || speedText) && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                fontFamily: '"CircularXX", sans-serif',
                color: BreakfastColors.subtitle,
                marginBottom: 1.5,
                display: 'block'
              }}
            >
              {formatBytes(uploadedBytes)} of {formatBytes(totalBytes)} uploaded
              {speedText ? ` · ${speedText}` : ''}
            </Typography>
          )}
          
          <BreakfastProgressBar value={totalProgress} />
          
          {failed > 0 && (
            <Box mt={1.5}>
              <Typography 
                variant="caption"
                sx={{ 
                  fontSize: '12px',
                  fontWeight: 500,
                  fontFamily: '"CircularXX", sans-serif',
                  color: BreakfastColors.error,
                  display: 'block',
                  marginBottom: 1
                }}
              >
                {failed} photo{failed !== 1 ? 's' : ''} did not finish. This is usually caused by a weak connection.
              </Typography>
              {onRetryFailed && (
                <BreakfastButton
                  title={isRetryingFailedBatch ? 'Retrying Failed Photos...' : `Retry ${failed} Failed Photo${failed !== 1 ? 's' : ''}`}
                  style={BreakfastButtonStyle.Secondary}
                  size={BreakfastButtonSize.Small}
                  onPressed={isRetryingFailedBatch ? undefined : onRetryFailed}
                />
              )}
            </Box>
          )}
        </Box>
      </BreakfastCard>
    );
  };

  const wrapPhotoSelectControl = (
    button: React.ReactNode,
    enabled: boolean,
  ) => {
    if (!onFileInputChange || !enabled) {
      return button;
    }

    return (
      <Box
        sx={{
          position: 'relative',
          display: 'block',
          width: '100%',
          cursor: enabled ? 'pointer' : 'default',
        }}
      >
        <Box sx={{ pointerEvents: 'none' }}>{button}</Box>
        <Box
          component="input"
          type="file"
          multiple
          accept="image/*"
          aria-label="Select photos"
          onChange={onFileInputChange}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            zIndex: 2,
            opacity: 0.01,
            cursor: 'pointer',
          }}
        />
      </Box>
    );
  };

  const renderPhotoSelectButton = (
    title: string,
    enabled = true,
  ) => wrapPhotoSelectControl(
    <BreakfastButton
      title={title}
      style={BreakfastButtonStyle.Primary}
      size={BreakfastButtonSize.Large}
      onPressed={enabled ? onFileSelect : undefined}
      leftIcon={<AddPhotoIcon sx={{ fontSize: 16 }} />}
    />,
    enabled,
  );

  const renderAddMorePhotosButton = (enabled: boolean) => wrapPhotoSelectControl(
    <BreakfastButton
      title="Add More Photos"
      style={BreakfastButtonStyle.Secondary}
      size={BreakfastButtonSize.Large}
      onPressed={enabled ? onFileSelect : undefined}
      leftIcon={enabled ?
        <AddPhotoIcon sx={{ fontSize: 16 }} /> :
        <CircularProgress size={16} sx={{ color: 'inherit' }} />
      }
    />,
    enabled,
  );

  const renderUploadControls = () => {
    if (!session.isValid || sessionCompleted || allUploadsComplete || uploadsStarted) return null;
    if (flowMode && hasSelection) return null;

    return (
      <Box
        px={flowMode ? 0 : { xs: 2, sm: 3 }}
        sx={{
          display: flowMode && !isDesktop && !hasSelection ? 'none' : 'block',
        }}
      >
        <Box>
          {hasSelection && !flowMode && (
            <Box mb={1.5}>
              <BreakfastCard
                backgroundColor={BreakfastColors.primaryFill}
                borderColor={BreakfastColors.primary + '33'}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '24px',
                      backgroundColor: BreakfastColors.primary + '1A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PhotoLibraryIcon
                      sx={{
                        fontSize: 24,
                        color: BreakfastColors.primary,
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '16px',
                        fontWeight: 700,
                        fontFamily: '"CircularXX", sans-serif',
                        color: BreakfastColors.title,
                        letterSpacing: '-0.2px',
                        lineHeight: 1.3,
                        marginBottom: 0.5,
                      }}
                    >
                      {uploads.length} photo{uploads.length !== 1 ? 's' : ''} selected
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        fontFamily: '"CircularXX", sans-serif',
                        color: BreakfastColors.subtitle,
                        letterSpacing: '-0.1px',
                        lineHeight: 1.4,
                      }}
                    >
                      Ready to upload
                    </Typography>
                  </Box>
                </Box>
              </BreakfastCard>
            </Box>
          )}
          {hasSelection && flowMode && (
            <Typography
              sx={{
                textAlign: 'center',
                mb: 2,
                color: StudioFlowColors.subtitle,
                fontSize: 15,
                fontFamily: StudioFlowColors.bodyFontFamily,
              }}
            >
              {uploads.length} photo{uploads.length !== 1 ? 's' : ''} selected
            </Typography>
          )}
          
          {/* Action buttons */}
          <Stack spacing={1}>
            {!hasSelection ? (
              // Photo source selection buttons (when no photos selected)
              <>
                {renderPhotoSelectButton('Select Photos')}
                {/* TODO: Re-enable after Google Cloud OAuth verification complete
                <BreakfastButton
                  title="Import from Google Photos"
                  style={BreakfastButtonStyle.Secondary}
                  size={BreakfastButtonSize.Large}
                  onPressed={onGooglePhotosSelect}
                  leftIcon={<GooglePhotosCloudIcon sx={{ fontSize: 16 }} />}
                />
                */}
              </>
            ) : !uploadsStarted ? (
              // Start Upload and Add More buttons (when photos selected but not uploading)
              <>
                <BreakfastButton
                  title={!hasValidPhotoCount ? 
                    (!meetsMinimum ? `Need ${minPhotosRequired - uploads.length} More Photos` : 
                     exceedsMaximum ? `Remove ${uploads.length - maxPhotosAllowed} Photos` : 'Start Upload') :
                    (isBatchNumberLoaded ? 'Start Upload' : 'Preparing...')
                  }
                  style={BreakfastButtonStyle.Primary}
                  size={BreakfastButtonSize.Large}
                  onPressed={isBatchNumberLoaded && hasValidPhotoCount ? onStartUpload : undefined}
                  leftIcon={isBatchNumberLoaded ? 
                    <CloudUploadIcon sx={{ fontSize: 16 }} /> :
                    <CircularProgress size={16} sx={{ color: 'inherit' }} />
                  }
                />
                
                {renderAddMorePhotosButton(preparingCount === 0)}
                
                {/* TODO: Re-enable after Google Cloud OAuth verification complete
                <BreakfastButton
                  title="Add from Google Photos"
                  style={BreakfastButtonStyle.Secondary}
                  size={BreakfastButtonSize.Large}
                  onPressed={preparingCount > 0 ? undefined : onGooglePhotosSelect}
                  leftIcon={preparingCount > 0 ?
                    <CircularProgress size={16} sx={{ color: 'inherit' }} /> :
                    <GooglePhotosCloudIcon sx={{ fontSize: 16 }} />
                  }
                />
                */}
                
                <BreakfastButton
                  title="Clear"
                  style={BreakfastButtonStyle.Secondary}
                  size={BreakfastButtonSize.Large}
                  onPressed={onClearAll}
                  leftIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                  destructive
                />
              </>
            ) : null}
          </Stack>
        </Box>
      </Box>
    );
  };

  const renderUploadSuccessMessage = () => {
    if (!allUploadsComplete || sessionCompleted) return null;
    
    // Show thank you message after Make My Book is clicked
    if (makeMyBookClicked) {
      return (
        <BreakfastCard backgroundColor={BreakfastColors.success + '1A'}>
          <Box textAlign="center" py={3}>
            <CheckCircleIcon 
              sx={{ 
                fontSize: 64, 
                color: BreakfastColors.success,
                marginBottom: 2
              }} 
            />
            <Typography 
              variant="body2"
              sx={{ 
                fontSize: '20px',
                fontWeight: 700,
                fontFamily: '"CircularXX", sans-serif',
                color: BreakfastColors.title,
                marginBottom: 1
              }}
            >
              Thank you!
            </Typography>
            <Typography 
              variant="body1"
              sx={{ 
                fontSize: '16px',
                fontWeight: 400,
                fontFamily: '"CircularXX", sans-serif',
                color: BreakfastColors.subtitle,
              }}
            >
              We have your photos and will get back to you shortly!
            </Typography>
          </Box>
        </BreakfastCard>
      );
    }
    
    if (flowMode) {
      return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <FlowLoadingOverlay message="Upload complete — continuing" />
        </Box>
      );
    }

    return (
      <BreakfastCard backgroundColor={BreakfastColors.success + '1A'}>
        <Box textAlign="center" py={2}>
          <CheckCircleIcon 
            sx={{ 
              fontSize: 48, 
              color: BreakfastColors.success,
              marginBottom: 2
            }} 
          />
          <Typography 
            variant="body2"
            sx={{ 
              fontSize: '16px',
              fontWeight: 700,
              fontFamily: '"CircularXX", sans-serif',
              color: BreakfastColors.title,
              marginBottom: 1
            }}
          >
            Upload Complete!
          </Typography>
          <Typography 
            variant="body1"
            sx={{ 
              fontSize: '14px',
              fontWeight: 400,
              fontFamily: '"CircularXX", sans-serif',
              color: BreakfastColors.subtitle,
              marginBottom: 3
            }}
          >
            All {uploads.length} photos uploaded successfully
          </Typography>
          {onMakeMyBook && (
            <BreakfastButton
              title="Make My Book"
              style={BreakfastButtonStyle.Primary}
              size={BreakfastButtonSize.Large}
              onPressed={handleMakeMyBook}
            />
          )}
        </Box>
      </BreakfastCard>
    );
  };

  const flowContent = (
    <>
      <Box
        sx={{
          maxWidth: 480,
          margin: '0 auto',
          pb: { xs: !hasSelection && !uploadsStarted ? 14 : 0, md: 0 },
        }}
      >
        <Stack spacing={2}>
          {renderFlowUploadProgress()}
          {renderFlowUploadPlaceholder()}
          {renderPhotoRequirementsWarning()}
          {renderUploadControls()}
          {renderUploadSuccessMessage()}
        </Stack>
      </Box>
      {!isDesktop && !hasSelection && !uploadsStarted && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            bgcolor: StudioFlowColors.background,
            px: 3,
            pt: 2,
            pb: 'calc(16px + env(safe-area-inset-bottom))',
          }}
        >
          {renderPhotoSelectButton('Select Photos')}
        </Box>
      )}
    </>
  );

  if (flowMode) {
    return flowContent;
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        backgroundColor: BreakfastColors.background,
        py: 2,
      }}
    >
      <Container 
        maxWidth="sm" 
        sx={{ 
          px: { xs: 1, sm: 3 },
        }}
      >
        <Box
          sx={{
            maxWidth: 700,
            margin: '0 auto',
            px: { xs: 1, sm: 2 },
          }}
        >
          <Stack spacing={3}>
            {renderHeader()}
            {renderStatusCard()}
            
            {/* Photo requirements warning */}
            {renderPhotoRequirementsWarning()}
            
            {/* Upload progress (while uploading) */}
            {renderMainUploadProgressBar()}
            
            {/* Upload controls */}
            {renderUploadControls()}
            
            {/* Upload success message */}
            {renderUploadSuccessMessage()}
            
            {/* Bottom padding for pinned content */}
            {(allUploadsComplete || makeMyBookClicked) && (
              <Box height={120} />
            )}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};
